import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import api from '../../api/client';
import StatusBadge from '../common/StatusBadge';
import ConfirmDialog from '../common/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';

const STATUS_FILTERS = [
  { value: 'all', label: 'All Campaigns' },
  { value: 'active', label: 'Active' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed', label: 'Completed' },
  { value: 'paused', label: 'Paused' },
];

export default function CampaignList() {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmItem, setConfirmItem] = useState(null);

  useEffect(() => {
    fetchCampaigns();
  }, [statusFilter, search]);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      const { data } = await api.get('/api/campaigns', { params });
      setCampaigns(data);
    } catch (err) {
      console.error('[CampaignList] Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (campaign) => {
    try {
      const { data } = await api.put(`/api/campaigns/${campaign.id}`, {
        is_active: !campaign.is_active,
      });
      setCampaigns(campaigns.map((c) =>
        c.id === campaign.id ? { ...c, is_active: data.is_active } : c
      ));
    } catch (err) {
      console.error('[CampaignList] Toggle failed:', err);
    }
  };

  const handleDelete = async () => {
    if (!confirmItem) return;
    try {
      await api.delete(`/api/campaigns/${confirmItem.id}`);
      setCampaigns(campaigns.filter((c) => c.id !== confirmItem.id));
    } catch (err) {
      console.error('[CampaignList] Delete failed:', err);
    } finally {
      setConfirmOpen(false);
      setConfirmItem(null);
    }
  };

  const getProgress = (campaign) => {
    if (!campaign.play_target || !campaign.play_count) return 0;
    return Math.min((campaign.play_count / campaign.play_target) * 100, 100);
  };

  const getBudgetProgress = (campaign) => {
    if (!campaign.budget || !campaign.spent) return 0;
    return Math.min((campaign.spent / campaign.budget) * 100, 100);
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Loading campaigns...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">Campaigns</h1>
        <Link to="/advertising/campaigns/create" className="btn btn-primary btn-sm">
          + New Campaign
        </Link>
      </div>

      <div className="flex flex-wrap gap-3">
        <input
          type="text"
          placeholder="Search campaigns..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input max-w-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input w-40 text-sm"
        >
          {STATUS_FILTERS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {campaigns.length === 0 ? (
        <div className="py-12 text-center text-dark-400">
          No campaigns found.
          <Link to="/advertising/campaigns/create" className="ml-2 text-primary-400 hover:text-primary-300">
            Create one
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map((campaign) => (
            <div key={campaign.id} className="rounded-xl bg-dark-850 border border-dark-700 p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-dark-100">{campaign.name}</h3>
                    <StatusBadge status={campaign.status} size="sm" />
                  </div>
                  <p className="mt-1 text-sm text-dark-400">
                    {campaign.advertiser_name && `Advertiser: ${campaign.advertiser_name}`}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Link
                    to={`/advertising/campaigns/${campaign.id}/edit`}
                    className="text-sm text-dark-400 hover:text-dark-200"
                    title="Edit"
                  >
                    ✎
                  </Link>
                  <button
                    onClick={() => {
                      setConfirmItem(campaign);
                      setConfirmOpen(true);
                    }}
                    className="text-sm text-error-400 hover:text-error-300"
                    title="Delete"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="mt-4 space-y-3">
                <div>
                  <div className="flex justify-between text-xs text-dark-400 mb-1">
                    <span>Play count: {campaign.play_count || 0} / {campaign.play_target || 0}</span>
                    <span>{Math.round(getProgress(campaign))}%</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-dark-700 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary-600 transition-all"
                      style={{ width: `${getProgress(campaign)}%` }}
                    />
                  </div>
                </div>

                {campaign.budget && (
                  <div>
                    <div className="flex justify-between text-xs text-dark-400 mb-1">
                      <span>Budget: ${campaign.spent || 0} / ${campaign.budget}</span>
                      <span>{Math.round(getBudgetProgress(campaign))}%</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-dark-700 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-warning-500 transition-all"
                        style={{ width: `${getBudgetProgress(campaign)}%` }}
                      />
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between text-xs text-dark-400">
                  <span>
                    {campaign.start_date && campaign.end_date
                      ? `${format(parseISO(campaign.start_date), 'PP')} – ${format(parseISO(campaign.end_date), 'PP')}`
                      : '—'}
                  </span>
                  <button
                    onClick={() => handleToggleActive(campaign)}
                    className="text-xs text-dark-500 hover:text-dark-300"
                  >
                    {campaign.is_active ? 'Pause' : 'Activate'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Delete Campaign"
        message={`Are you sure you want to delete "${confirmItem?.name}"? This action cannot be undone.`}
        danger
        onConfirm={handleDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
}
